/**
 * Die USt-IdNr.-Bestaetigung als Anzeige — EINE reine Regel.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 3.3 („USt-IdNr.-Pruefung"), § 6.5 („Speichern
 * duerfen wir: Pruefergebnis + Zeitpunkt"), § 2.2 („Quelle + Stand an JEDER Angabe"), § 4.2 („ein
 * Zustand, ein Satz"). Kriterium R3.2 in docs/LOOP-RECHERCHE.md.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  DIE EINE REGEL DIESES PAKETS: MOMENTAUFNAHME, NIE ZUSTAND
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * Eine Bestaetigungsabfrage nach § 18e UStG sagt: „**am 07.08.2026 um 09:12 Uhr** wurde
 * bestaetigt, dass ...". Sie sagt NICHT: „diese Nummer ist gueltig". Der Unterschied ist kein
 * Feinschliff, sondern der ganze Punkt: der Betreiber stuetzt darauf eine steuerliche
 * Entscheidung. Liest er ein „gültig" ohne Datum, haelt er einen Nachweis von vorgestern fuer eine
 * Auskunft von heute.
 *
 * Umgesetzt ist das hier so, dass es nicht versehentlich verloren gehen kann:
 *
 *  1. **`ergebnis` ist NIE ein Satz ohne Datum.** Jeder Ergebnistext wird aus `bestaetigtAm` und
 *     dem Wortlaut der Behoerde ZUSAMMENGESETZT; fehlt der Zeitpunkt, gibt es kein Ergebnis,
 *     sondern den Zustand „ohne Zeitpunkt kein Nachweis". Es gibt keinen Pfad, auf dem ein
 *     Ergebnissatz ohne Datum entsteht (`apps/api/test/ustid.test.ts` prueft alle).
 *  2. **Es gibt kein `gueltig`-Feld** — weder hier noch im Schema (sql/238, Entscheidung 2). Was
 *     bestaetigt wurde, steht als Wortlaut da, nicht als Wahrheitswert.
 *  3. **`momentaufnahme` steht immer daneben** — ein fester Satz, der die Reichweite der Aussage
 *     benennt. Er ist Teil der Karte, nicht eine Option der Oberflaeche.
 *
 * ── Warum eine reine Funktion und kein JSX ────────────────────────────────────────────────────
 * `apps/web` hat seit dem 06.08.2026 zwar einen Test-Runner, aber eine Regel mit Rechtsfolgen
 * gehoert trotzdem in ein Paket: dort ist sie ohne DOM pruefbar, und beide Seiten importieren
 * dieselbe. Die Web-Tests sind die Ebene darueber (rendert die Komponente auch das, was die Regel
 * sagt?) — `apps/web/test/ustid-karte.test.tsx`. Dieses Paket hat bewusst KEIN `test`-Skript
 * (`apps/api/test/ci-testabdeckung.test.ts`); seine Tests liegen in `apps/api/test/ustid.test.ts`.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zone: `jetzt` und `zeitzone` sind Parameter.
 */
import {
  anreicherungsAnzeige,
  type AnreicherungsHandlung,
  type AnreicherungsZeile,
  type AnreicherungsZustand,
} from "@maestro/anreicherung";
import { standDatum, standZeile } from "@maestro/recherche-stand";

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/** Vergleichsergebnis je Feld der QUALIFIZIERTEN Abfrage (Buchstaben A–D des BZSt). */
export interface UstIdAbgleichRoh {
  firmenname?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
}

/**
 * Die Zeile aus `ustid_bestaetigungen`, wie die API sie als JSON ausliefert (Zeitstempel als
 * ISO-Strings) — Lebenszyklus-Felder wie in `@maestro/anreicherung`.
 */
export interface UstIdZeile extends AnreicherungsZeile {
  /** Die abgefragte Nummer. */
  angefragteUstid?: string | null;
  /** Die ANFRAGENDE Nummer des Betriebs — ohne sie ist der Datensatz kein Nachweis. */
  anfragendeUstid?: string | null;
  abfrageart?: string | null;
  /** Der Statuscode der Behoerde (`evatr-0000` …). */
  ergebnisStatus?: string | null;
  /** Der deutsche Wortlaut der Behoerde zu diesem Code. */
  ergebnisText?: string | null;
  /** Der `anfrageZeitpunkt` AUS DER ANTWORT — der Moment, fuer den die Aussage gilt. */
  bestaetigtAm?: string | null;
  /** Angaben ueber die NUMMER (nicht ueber diese Zeile). */
  nummerGueltigAb?: string | null;
  nummerGueltigBis?: string | null;
  abgleich?: UstIdAbgleichRoh | null;
}

export interface UstIdKarteLage {
  /** Mandanten-Schalter des Moduls (`tenantSettings.ustidEnabled`). */
  modulAktiv: boolean;
  /** Firmenname, zu dem bestaetigt wurde. */
  firma: string;
  /** Die USt-IdNr. des Kunden — `null`/leer heisst: es gibt nichts zu bestaetigen. */
  ustIdNr?: string | null;
  /** Die EIGENE Nummer des Betriebs. Fehlt sie, ist die Pruefung NICHT EINGERICHTET. */
  eigeneUstIdNr?: string | null;
  /** Die Zeile — `null` heisst: es gibt gar keine („nie gefragt"). */
  zeile: UstIdZeile | null;
  jetzt: Date;
  zeitzone?: string;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/** Der Anzeigename des Moduls — exakt wie er in der Modul-Kachel steht. */
export const MODUL_USTID = "USt-IdNr.-Bestätigung";

/** Die Quelle, unter der jede dieser Angaben ausgewiesen wird (`@maestro/recherche-stand`). */
export const QUELLE_USTID = "USt-IdNr.-Bestätigung" as const;

/**
 * Der Satz, der IMMER neben einem Ergebnis steht. Er ist der Kern des Kriteriums und deshalb eine
 * Konstante, kein Vorschlag: die Aussage der Behoerde gilt fuer einen Zeitpunkt, nicht fuer heute.
 */
export const MOMENTAUFNAHME_HINWEIS =
  "Das ist eine Momentaufnahme für genau diesen Zeitpunkt — ob die Nummer heute noch gilt, sagt sie nicht. " +
  "Für einen aktuellen Nachweis erneut bestätigen lassen.";

/** Der Satz, wenn der Betrieb seine eigene Nummer nicht hinterlegt hat. */
export const NICHT_EINGERICHTET =
  "Die Bestätigungsabfrage ist für diesen Betrieb nicht eingerichtet: Es fehlt die eigene USt-IdNr. " +
  "Eine Bestätigung gilt immer nur für den Anfragenden — deshalb wird sie nie mit einer fremden Nummer geholt.";

export interface UstIdErgebnisZeile {
  /** Feldbezeichnung, wie sie in der Karte steht. */
  feld: string;
  /** Der Wert. */
  wert: string;
}

export interface UstIdKarte {
  zustand: AnreicherungsZustand;
  /** Genau ein Satz zum Zustand — `null`, wenn das Ergebnis selbst spricht. */
  satz: string | null;
  handlung: AnreicherungsHandlung | null;
  /**
   * **Die Kernaussage, immer datiert.** `null`, wenn (noch) keine Bestaetigung vorliegt. Nie ein
   * Satz ohne Zeitpunkt — der Zeitpunkt steht am Anfang, nicht als Fussnote.
   */
  ergebnis: string | null;
  /** Steht bei JEDEM `ergebnis` dabei. `null`, wenn es keins gibt. */
  momentaufnahme: string | null;
  /** Belegzeilen: abgefragte Nummer, anfragende Nummer, Art der Abfrage, Abgleich, Zeitraeume. */
  zeilen: UstIdErgebnisZeile[];
  /** „USt-IdNr.-Bestätigung · Stand 07.08.2026" — der Stand UNSERER Zeile (R1.6). */
  herkunft: string | null;
}

/** Die Beschriftung je Feld des qualifizierten Abgleichs. */
const ABGLEICH_FELDER: { schluessel: keyof UstIdAbgleichRoh; label: string }[] = [
  { schluessel: "firmenname", label: "Firmenname" },
  { schluessel: "strasse", label: "Straße" },
  { schluessel: "plz", label: "PLZ" },
  { schluessel: "ort", label: "Ort" },
];

/** Der Wortlaut der Buchstaben A–D — der Betreiber liest keine Buchstaben, sondern Saetze. */
const ABGLEICH_WORTLAUT: Readonly<Record<string, string>> = Object.freeze({
  A: "stimmt überein",
  B: "stimmt NICHT überein",
  C: "nicht angefragt",
  D: "vom Mitgliedstaat nicht mitgeteilt",
});

/** „07.08.2026, 09:12" — Bestaetigungen brauchen die Minute, nicht nur den Tag. */
function moment(iso: string | null | undefined, zeitzone?: string): string | null {
  return standDatum(iso, zeitzone, "minute");
}

const leereKarte = {
  ergebnis: null,
  momentaufnahme: null,
  zeilen: [] as UstIdErgebnisZeile[],
  herkunft: null,
};

/**
 * Bildet die Datenlage auf eine Karte ab.
 *
 * Reihenfolge der Pruefungen:
 *  1. Modul aus schlaegt alles (wie in `@maestro/anreicherung`).
 *  2. **Eigene Nummer fehlt** — die Pruefung ist NICHT EINGERICHTET. Steht VOR allem anderen:
 *     ob eine Zeile existiert, ist unerheblich, wenn strukturell nicht gefragt werden kann. Ohne
 *     Handlung, denn der Weg fuehrt in die Modul-Kachel — dorthin zeigt bereits `modul_kachel`.
 *  3. **Keine USt-IdNr. des Kunden** — sauberer Zustand, kein Fehler, keine Handlung: ein Knopf
 *     „Jetzt bestätigen" ohne Nummer waere eine Schaltflaeche ohne Wirkung.
 *  4. Sonst die gemeinsame Zustandsabbildung — und darueber das datierte Ergebnis.
 */
export function ustIdKarte(lage: UstIdKarteLage): UstIdKarte {
  const eigene = (lage.eigeneUstIdNr ?? "").trim();
  const fremde = (lage.ustIdNr ?? "").trim();

  if (lage.modulAktiv && !eigene) {
    return { zustand: "nie_gefragt", satz: NICHT_EINGERICHTET, handlung: null, ...leereKarte };
  }
  if (lage.modulAktiv && !fremde) {
    return {
      zustand: "nie_gefragt",
      satz: `Zu „${lage.firma}" ist keine USt-IdNr. hinterlegt — es gibt nichts zu bestätigen.`,
      handlung: null,
      ...leereKarte,
    };
  }

  const anzeige = anreicherungsAnzeige({
    modulAktiv: lage.modulAktiv,
    modulName: MODUL_USTID,
    firma: lage.firma,
    zeile: lage.zeile,
    jetzt: lage.jetzt,
    zeitzone: lage.zeitzone,
  });

  const zeile = lage.zeile;
  const hatErgebnis = Boolean(zeile && (zeile.status === "found" || zeile.status === "not_found"));
  if (!zeile || !hatErgebnis) {
    return { zustand: anzeige.zustand, satz: anzeige.satz, handlung: anzeige.handlung, ...leereKarte };
  }

  // ── Die Kernaussage. Ohne Zeitpunkt gibt es sie NICHT. ────────────────────────────────────
  const zeitpunkt = moment(zeile.bestaetigtAm, lage.zeitzone);
  const wortlaut = (zeile.ergebnisText ?? "").trim();
  if (!zeitpunkt || !wortlaut) {
    // Kann im Normalbetrieb nicht entstehen (der CHECK in sql/238 laesst ein Ergebnis ohne
    // `bestaetigt_am` nicht zu). Trotzdem ausformuliert statt weggelassen: eine Karte, die einen
    // Nachweis behauptet, den sie nicht datieren kann, waere genau der Fehler, den R3.2 abstellt.
    return {
      zustand: "fehler",
      satz: "Zu dieser Bestätigung fehlt der Zeitpunkt — ohne ihn ist sie kein Nachweis. Bitte erneut bestätigen lassen.",
      handlung: anzeige.handlung,
      ...leereKarte,
    };
  }

  const zeilen: UstIdErgebnisZeile[] = [];
  if (fremde) zeilen.push({ feld: "Geprüfte USt-IdNr.", wert: fremde });
  // Die anfragende Nummer gehoert SICHTBAR in den Beleg: eine Bestaetigung gilt fuer den
  // Anfragenden — wer sie liest, muss sehen, fuer wen sie gilt (GATE-D, 07.08.2026).
  if (zeile.anfragendeUstid) zeilen.push({ feld: "Angefragt mit eigener USt-IdNr.", wert: zeile.anfragendeUstid });
  if (zeile.abfrageart) {
    zeilen.push({
      feld: "Art der Abfrage",
      wert:
        zeile.abfrageart === "qualifiziert"
          ? "qualifiziert (mit Abgleich der Firmendaten)"
          : "einfach (nur die Nummer)",
    });
  }
  // Zeitraeume der NUMMER — ausdruecklich als solche beschriftet, damit sie niemand fuer die
  // Haltbarkeit unserer Zeile haelt (sql/238, Entscheidung 3).
  const ab = standDatum(zeile.nummerGueltigAb, lage.zeitzone);
  const bis = standDatum(zeile.nummerGueltigBis, lage.zeitzone);
  if (ab) zeilen.push({ feld: "Nummer laut Behörde gültig ab", wert: ab });
  if (bis) zeilen.push({ feld: "Nummer laut Behörde gültig bis", wert: bis });

  for (const { schluessel, label } of ABGLEICH_FELDER) {
    const buchstabe = (zeile.abgleich?.[schluessel] ?? "").toString().trim().toUpperCase();
    if (!buchstabe) continue;
    zeilen.push({ feld: `Abgleich ${label}`, wert: ABGLEICH_WORTLAUT[buchstabe] ?? buchstabe });
  }

  return {
    zustand: anzeige.zustand,
    satz: anzeige.satz,
    handlung: anzeige.handlung,
    // Der Zeitpunkt steht VORNE. „Bestätigt am …: <Wortlaut der Behörde>" — nie „gültig" allein.
    ergebnis: `Am ${zeitpunkt} Uhr vom Bundeszentralamt für Steuern bestätigt: ${wortlaut}`,
    momentaufnahme: MOMENTAUFNAHME_HINWEIS,
    zeilen,
    // Der Stand UNSERER Zeile (wann WIR gefragt haben) — nicht zu verwechseln mit `bestaetigtAm`.
    // Gebaut aus `standZeile` (R1.6), nicht von Hand: sonst gaebe es eine zweite Meinung darueber,
    // was ohne Zeitstempel passiert.
    herkunft: standZeile(QUELLE_USTID, zeile.lookedUpAt, lage.zeitzone),
  };
}
